import { AlignVAT, DifferentialPrice, DifferentialPriceForQuantity, DifferentialRangePrice, FromPrice, MinimumOrderableQuantity, PriceDisplayMode, PriceInfo, RangePrice } from "@vp/ep-pricing";
import React, { JSX } from "react";
import { SHOW_ELEV8_WITH_TOTALPRICE_ON_CONSUMER } from "../../constants/featureFlags";
import { useFeatureToggle } from "../../hooks/useFeatureToggle";
import { productPricingService } from "../../services/productPricingService";
import { Locale } from "../../types/locales";
import { PriceDisplayType, PriceProperties, Prices } from "../../types/pricing/price-display.types";
import { PriceConfigBuilder } from "../../util/price-config";
import { SetVersionOnWindow } from "../../util/version-logger";

export interface PriceDisplayWrapperProps {
  priceDisplayType: PriceDisplayType;
  prices: Prices;
  priceProperties: PriceProperties;
  placeholderText?: string;
}

const PriceDisplayWrapperCore = ({
  priceDisplayType,
  prices,
  priceProperties,
  placeholderText = "",
}: PriceDisplayWrapperProps) => {
  SetVersionOnWindow();
  // Set default for price properties
  const normalizedPriceProperties: PriceProperties = {
    ...priceProperties,
    hideSetupFee: priceProperties.hideSetupFee ?? false,
    alignVAT: priceProperties.alignVAT ?? AlignVAT.BOTTOM,
    isTile: priceProperties.isTile ?? false,
    hidePriceDetails: priceProperties.hidePriceDetails ?? false,
  };
  const locale = productPricingService.locale;

  const { isFeatureFlagEnabled } = useFeatureToggle();
  const showElev8WithTotalPriceOnConsumer = isFeatureFlagEnabled(SHOW_ELEV8_WITH_TOTALPRICE_ON_CONSUMER);

  if (!prices) return null;
  const builder = new PriceConfigBuilder(prices, normalizedPriceProperties, locale);
  const componentsByType: Record<PriceDisplayType, () => JSX.Element | null> = {
    [PriceDisplayType.PriceInfo]: () => {
      const config = builder.getCombinedConfig(PriceDisplayMode.TOTAL);
      return config ? <PriceInfo placeholdertext={placeholderText} priceInfoConfig={config} doesOfferExist={priceProperties.doesOfferExist} alignVAT={normalizedPriceProperties.alignVAT} isTile={normalizedPriceProperties.isTile} hidePriceDetails={normalizedPriceProperties.hidePriceDetails} /> : null;
    },
    [PriceDisplayType.UnitPriceInfo]: () => {
      const config = builder.getCombinedConfig(PriceDisplayMode.UNIT);
      return config ? <PriceInfo placeholdertext={placeholderText} priceInfoConfig={config} doesOfferExist={priceProperties.doesOfferExist} showTotalPriceForEachVariant={showElev8WithTotalPriceOnConsumer} alignVAT={normalizedPriceProperties.alignVAT} isTile={normalizedPriceProperties.isTile} /> : null;
    },
    [PriceDisplayType.FromPrice]: () => {
      const config = builder.getCombinedConfig(PriceDisplayMode.TOTAL);
      return config ? <FromPrice placeholdertext={placeholderText} fromPriceConfig={config} isTile={normalizedPriceProperties.isTile} /> : null;
    },
    [PriceDisplayType.UnitFromPrice]: () => {
      const config = builder.getCombinedConfig(PriceDisplayMode.UNIT);
      return config ? <FromPrice placeholdertext={placeholderText} fromPriceConfig={config} showTotalPriceForEachVariant={showElev8WithTotalPriceOnConsumer} isTile={normalizedPriceProperties.isTile} /> : null;
    },
    [PriceDisplayType.DifferentialUnitRangePrice]: () => {
      const config = builder.getDifferentialRangePriceConfig();
      return config ? <DifferentialRangePrice placeholdertext={placeholderText} differentialRangePriceConfig={config} /> : null;
    },
    [PriceDisplayType.DifferentialPrice]: () => {
      const config = builder.getDifferentialConfig(PriceDisplayMode.TOTAL);
      return config ? <DifferentialPrice placeholdertext={placeholderText} differentialPriceConfig={config} /> : null;
    },
    [PriceDisplayType.UnitDifferentialPrice]: () => {
      const config = builder.getDifferentialConfig(PriceDisplayMode.UNIT);
      return config ? <DifferentialPrice placeholdertext={placeholderText} differentialPriceConfig={config} /> : null;
    },
    [PriceDisplayType.DifferentialPriceForQuantity]: () => {
      const config = builder.getDifferentialForQuantityConfig();
      return config ? <DifferentialPriceForQuantity placeholdertext={placeholderText} differentialPriceForQuantityConfig={config} /> : null;
    },
    [PriceDisplayType.UnitRangePrice]: () => {
      const config = builder.getRangePriceConfig(normalizedPriceProperties.isTile);
      return config ? (
        <>
          <RangePrice placeholdertext={placeholderText} rangePriceConfig={config} />
          {!priceProperties.hideMinimumOrderableQuantity && normalizedPriceProperties.quantity && (
            <MinimumOrderableQuantity placeholdertext={placeholderText} quantity={normalizedPriceProperties.quantity} locale={locale as Locale} alwaysShowMOQInfo={normalizedPriceProperties.alwaysShowMOQInfo} />
          )}
        </>
      ) : null;
    },
    [PriceDisplayType.LogoProcessingCharge]: () => {
      const config = builder.getDifferentialConfig(PriceDisplayMode.TOTAL);
      return config ? <DifferentialPrice placeholdertext={placeholderText} differentialPriceConfig={config} fontSkin={"body-standard"} textColor={"standard"} /> : null;
    },
  };

  return componentsByType[priceDisplayType]?.() ?? null;
};

const PriceDisplayWrapper = (priceDisplayWrapperProps: PriceDisplayWrapperProps) => {
  return (
    <PriceDisplayWrapperCore {...priceDisplayWrapperProps} />
  );
};

export { PriceDisplayWrapper };
